// Shown in place of the entity inspector when the selected node is gone
// because entity resolution folded it into another entity.
//
// A saved investigation keeps its own snapshot of every node, so a merged-away
// entity stays on the canvas and stays clickable. Without this the analyst gets
// a bare "failed to load"; here they get what happened and a way through to the
// entity that absorbed it.

import { Merge } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { mergeReasonSentence } from '@/features/entity-resolution/merge-reason'
import type { MergedEntityRedirect } from '@/features/entity-resolution/merged-redirect'

interface MergedEntityNoticeProps {
  redirect: MergedEntityRedirect
  onOpenCanonical: () => void
}

function formatWhen(iso: string | null): string | null {
  if (!iso) return null
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return null
  return when.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function MergedEntityNotice({ redirect, onOpenCanonical }: MergedEntityNoticeProps) {
  const { t } = useTranslation()
  // The survivor's display label is captured at merge time; fall back to the
  // element id's local part so the button never reads as an empty name.
  const canonicalName =
    redirect.canonicalNodeName?.trim() ||
    redirect.canonicalNodeId.split(':').pop() ||
    redirect.canonicalNodeId
  const when = formatWhen(redirect.mergedAt)
  // Known reasons get a readable phrase; anything else falls back to the raw
  // audit slug rather than hiding a reason we don't have a translation for.
  const reason = redirect.mergeReason ? mergeReasonSentence(t, redirect.mergeReason) : null

  return (
    <div className="m-3 rounded-md border border-border bg-muted/40 p-3">
      <div className="flex items-start gap-2">
        <Merge className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 space-y-1.5">
          <p className="text-2xs font-medium text-foreground">
            {t('investigations.entityDetail.merged.title')}
          </p>
          <p className="text-2xs leading-relaxed text-muted-foreground">
            {t('investigations.entityDetail.merged.body', { name: canonicalName })}
          </p>
          {(when || reason) && (
            <p className="text-2xs text-muted-foreground/70">
              {[when, reason].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      </div>
      <Button
        variant="secondary"
        size="sm"
        className="mt-2.5 h-7 w-full text-2xs"
        onClick={onOpenCanonical}
      >
        {t('investigations.entityDetail.merged.open', { name: canonicalName })}
      </Button>
    </div>
  )
}
